import { createEffect, createMemo, createSignal, type JSX, Show } from "solid-js";
import { Dialog } from "@ark-ui/solid";
import { IconButton, TextButton } from "../button/Button";
import { isNil } from "lodash-es";
import type { TranslationKey } from "../../state/i18n";
import { useTranslation } from "../../state/i18n";
import TextLink from "../link/TextLink";
import { condition } from "../../utils/solidUtils";
import { keyFromSelector } from "i18next";
import { useCurrentModal } from "./ModalManager";

type WizardModalProps = {
  title: string;
  content: () => JSX.Element;
  footer?: {
    confirm: {
      label: TranslationKey;
      action: () => Promise<void>;
      disabled: boolean;
    };
    showCloseButton: boolean;
    helpLink?: {
      label: TranslationKey;
      link: string;
    };
  };
};

const Wizard = (props: WizardModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { changeCloseBehaviour, canClose, close } = useCurrentModal();

  return (
    <div class="flex h-160 w-182 flex-col overflow-hidden rounded-lg bg-surface-default shadow-elevation-light-5">
      <div class="flex min-w-0 items-center justify-between border-b border-subtle py-1.5 pr-1.5 pl-4">
        <Dialog.Title class="truncate text-header-lg text-primary">{props.title}</Dialog.Title>
        <IconButton
          icon="x"
          aria-label={t(($) => $.CLOSE_ARIA)}
          size="large"
          variant="tertiary"
          disabled={!canClose()}
          onClick={() => close()}
        />
      </div>
      <Show when={props.footer} fallback={props.content()}>
        {(footer) => {
          const [isConfirming, setIsConfirming] = createSignal(false);
          const isConfirmDisabled = createMemo(() => footer().confirm.disabled || isConfirming());

          createEffect(() => {
            changeCloseBehaviour(isConfirming() ? "off" : "default");
          });

          return (
            <div class="flex h-full min-h-0 flex-col">
              <div class="min-h-0 grow overflow-y-auto">{props.content()}</div>
              <div class="flex shrink-0 items-center justify-between border-t border-subtle p-4">
                <Show when={condition(footer().helpLink, (helpLink) => !isNil(helpLink))} fallback={<div />}>
                  {(helpLink) => (
                    <div class="text-base-regular">
                      <TextLink href={helpLink().link} label={t(helpLink().label)} isExternal />
                    </div>
                  )}
                </Show>
                <div class="flex gap-2">
                  <Show when={footer().showCloseButton}>
                    <TextButton
                      size="large"
                      variant="secondary"
                      text={t(($) => $.CANCEL_BUTTON)}
                      disabled={!canClose()}
                      onClick={() => close()}
                    />
                  </Show>
                  <TextButton
                    size="large"
                    variant="primary"
                    text={t(footer().confirm.label ?? keyFromSelector(($) => $.CREATE_BUTTON))}
                    disabled={isConfirmDisabled()}
                    onClick={() => {
                      setIsConfirming(true);
                      footer()
                        .confirm.action()
                        .finally(() => {
                          setIsConfirming(false);
                        });
                    }}
                  />
                </div>
              </div>
            </div>
          );
        }}
      </Show>
    </div>
  );
};

export default Wizard;
